import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ZatcaConfigTab = () => {
  const [configData, setConfigData] = useState({
    apiEndpoint: "https://gw-fatoora.zatca.gov.sa/e-invoicing/developer-portal",
    clientId: "audit-ai-client-2024",
    clientSecret: "••••••••••••••••••••••••••••••••",
    certificatePath: "/certificates/zatca-prod.p12",
    certificatePassword: "••••••••••••",
    submissionMode: "production",
    autoSubmit: true,
    retryAttempts: "3",
    timeoutSeconds: "30"
  });

  const [connectionStatus, setConnectionStatus] = useState({
    status: 'connected',
    lastChecked: new Date(Date.now() - 120000),
    message: 'Successfully connected to ZATCA API'
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showSecrets, setShowSecrets] = useState({
    clientSecret: false,
    certificatePassword: false
  });

  const handleInputChange = (field, value) => {
    setConfigData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleSecretVisibility = (field) => {
    setShowSecrets(prev => ({
      ...prev,
      [field]: !prev?.[field]
    }));
  };

  const testConnection = async () => {
    setIsLoading(true);
    try {
      // Simulate API connection test
      await new Promise(resolve => setTimeout(resolve, 2000));
      setConnectionStatus({
        status: 'connected',
        lastChecked: new Date(),
        message: 'Connection test successful'
      });
    } catch (error) {
      setConnectionStatus({
        status: 'error',
        lastChecked: new Date(),
        message: 'Connection failed. Please check your credentials.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      console.log('ZATCA configuration saved:', configData);
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = () => {
    switch (connectionStatus?.status) {
      case 'connected':
        return <Icon name="CheckCircle" size={20} className="text-success" />;
      case 'error':
        return <Icon name="XCircle" size={20} className="text-error" />;
      default:
        return <Icon name="AlertCircle" size={20} className="text-warning" />;
    }
  };

  const getStatusColor = () => {
    switch (connectionStatus?.status) {
      case 'connected':
        return 'text-success';
      case 'error':
        return 'text-error';
      default:
        return 'text-warning';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">ZATCA Configuration</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure API credentials and submission settings for ZATCA compliance
          </p>
        </div>
        
        {/* Connection Status */}
        <div className="flex items-center space-x-3 bg-muted rounded-xl px-4 py-2">
          {getStatusIcon()}
          <div className="text-right">
            <p className={`text-sm font-medium ${getStatusColor()}`}>
              {connectionStatus?.status === 'connected' ? 'Connected' : 
               connectionStatus?.status === 'error' ? 'Connection Error' : 'Unknown'}
            </p>
            <p className="text-xs text-muted-foreground">
              {connectionStatus?.lastChecked?.toLocaleTimeString('en-GB')}
            </p>
          </div>
        </div>
      </div>
      {/* API Configuration */}
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
            <Icon name="Globe" size={20} className="mr-2 text-primary" />
            API Configuration
          </h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="lg:col-span-2">
              <Input
                label="API Endpoint"
                type="url"
                required
                value={configData?.apiEndpoint}
                onChange={(e) => handleInputChange('apiEndpoint', e?.target?.value)}
                placeholder="https://gw-fatoora.zatca.gov.sa/e-invoicing/developer-portal"
                description="ZATCA API endpoint URL"
              />
            </div>
            
            <Input
              label="Client ID"
              type="text"
              required
              value={configData?.clientId}
              onChange={(e) => handleInputChange('clientId', e?.target?.value)}
              placeholder="your-client-id"
              description="Provided by ZATCA developer portal"
            />
            
            <div className="relative">
              <Input
                label="Client Secret"
                type={showSecrets?.clientSecret ? "text" : "password"}
                required
                value={configData?.clientSecret}
                onChange={(e) => handleInputChange('clientSecret', e?.target?.value)}
                placeholder="Enter client secret"
                description="Keep this secure and confidential"
              />
              <button
                type="button"
                onClick={() => toggleSecretVisibility('clientSecret')}
                className="absolute right-3 top-8 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name={showSecrets?.clientSecret ? "EyeOff" : "Eye"} size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Certificate Configuration */}
        <div>
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
            <Icon name="Shield" size={20} className="mr-2 text-primary" />
            Certificate Configuration
          </h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Input
              label="Certificate Path"
              type="text"
              required
              value={configData?.certificatePath}
              onChange={(e) => handleInputChange('certificatePath', e?.target?.value)}
              placeholder="/path/to/certificate.p12"
              description="Path to your ZATCA certificate file"
            />
            
            <div className="relative">
              <Input
                label="Certificate Password"
                type={showSecrets?.certificatePassword ? "text" : "password"}
                required
                value={configData?.certificatePassword}
                onChange={(e) => handleInputChange('certificatePassword', e?.target?.value)}
                placeholder="Enter certificate password"
                description="Password for certificate file"
              />
              <button
                type="button"
                onClick={() => toggleSecretVisibility('certificatePassword')}
                className="absolute right-3 top-8 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name={showSecrets?.certificatePassword ? "EyeOff" : "Eye"} size={16} />
              </button>
            </div>
          </div>
          
          {/* Certificate Upload */}
          <div className="mt-4 p-4 border-2 border-dashed border-border rounded-xl">
            <div className="text-center">
              <Icon name="Upload" size={32} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-foreground font-medium">Upload New Certificate</p>
              <p className="text-xs text-muted-foreground mt-1">
                Drag and drop your .p12 certificate file or click to browse
              </p>
              <Button variant="outline" className="mt-3" iconName="FolderOpen">
                Browse Files
              </Button>
            </div>
          </div>
        </div>

        {/* Submission Settings */}
        <div>
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
            <Icon name="Settings" size={20} className="mr-2 text-primary" />
            Submission Settings
          </h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Submission Mode
              </label>
              <select
                value={configData?.submissionMode}
                onChange={(e) => handleInputChange('submissionMode', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:ring-2 focus:ring-ring focus:border-transparent"
              >
                <option value="sandbox">Sandbox (Testing)</option>
                <option value="production">Production</option>
              </select>
            </div>
            
            <Input
              label="Retry Attempts"
              type="number"
              value={configData?.retryAttempts}
              onChange={(e) => handleInputChange('retryAttempts', e?.target?.value)}
              min="1"
              max="10"
              description="Number of retry attempts"
            />
            
            <Input
              label="Timeout (seconds)"
              type="number"
              value={configData?.timeoutSeconds}
              onChange={(e) => handleInputChange('timeoutSeconds', e?.target?.value)}
              min="10"
              max="300"
              description="Request timeout duration"
            />
          </div>
          
          {/* Auto Submit Toggle */}
          <div className="mt-4 flex items-center justify-between p-4 bg-muted rounded-xl">
            <div>
              <h4 className="text-sm font-medium text-foreground">Auto-Submit Invoices</h4>
              <p className="text-xs text-muted-foreground mt-1">
                Automatically submit invoices to ZATCA after creation
              </p>
            </div>
            <button
              onClick={() => handleInputChange('autoSubmit', !configData?.autoSubmit)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                configData?.autoSubmit ? 'bg-primary' : 'bg-border'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  configData?.autoSubmit ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>
      {/* Connection Test */}
      <div className="bg-muted rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-foreground">Connection Test</h3>
            <p className="text-sm text-muted-foreground">
              Test your ZATCA API connection with current settings
            </p>
          </div>
          <Button
            variant="outline"
            loading={isLoading}
            onClick={testConnection}
            iconName="Zap"
            iconPosition="left"
          >
            Test Connection
          </Button>
        </div>
        
        <div className="flex items-center space-x-3 p-3 bg-card rounded-lg">
          {getStatusIcon()}
          <div>
            <p className={`text-sm font-medium ${getStatusColor()}`}>
              {connectionStatus?.message}
            </p>
            <p className="text-xs text-muted-foreground">
              Last checked: {connectionStatus?.lastChecked?.toLocaleString('en-GB')}
            </p>
          </div>
        </div>
      </div>
      {/* Actions */}
      <div className="flex items-center justify-between pt-6 border-t border-border">
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Lock" size={16} className="text-warning" />
          <span>Configuration changes require admin approval</span>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={() => window.location?.reload()}>
            Reset Changes
          </Button>
          <Button 
            variant="default" 
            loading={isLoading}
            onClick={handleSave}
            iconName="Save"
            iconPosition="left"
          >
            Save Configuration
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ZatcaConfigTab;

